import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import XPProgressHeader from '../../components/ui/XPProgressHeader';
import AIChatbot from '../../components/ui/AIChatbot';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ContentPreviewCard from './components/ContentPreviewCard';
import CategorySection from './components/CategorySection';
import SearchBar from './components/SearchBar';
import FeatureHighlight from './components/FeatureHighlight';
import RoleSelector from './components/RoleSelector';
import VideoTutorialCard from './components/VideoTutorialCard';
import DownloadableResource from './components/DownloadableResource';
import ProgressTracker from './components/ProgressTracker';

const RolePlaybookHub = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const contentPreviews = [
  {
    id: 1,
    title: "Junior Developer Onboarding Guide",
    description: "Complete walkthrough of your first 90 days including code review processes, development environment setup, and team collaboration workflows.",
    icon: "Code",
    category: "Getting Started",
    estimatedTime: "45 min read",
    locked: true
  },
  {
    id: 2,
    title: "Best Practices for Code Reviews",
    description: "Learn how to conduct effective code reviews, provide constructive feedback, and maintain code quality standards across the team.",
    icon: "GitPullRequest",
    category: "Development",
    estimatedTime: "30 min read",
    locked: true
  },
  {
    id: 3,
    title: "Performance Expectations & KPIs",
    description: "Understand the metrics used to evaluate your performance, quarterly goals, and career progression milestones for your role.",
    icon: "TrendingUp",
    category: "Performance",
    estimatedTime: "20 min read",
    locked: true
  },
  {
    id: 4,
    title: "Team Communication Protocols",
    description: "Master the communication channels, meeting schedules, and collaboration tools used by your team for efficient workflow.",
    icon: "MessageSquare",
    category: "Communication",
    estimatedTime: "15 min read",
    locked: true
  }];


  const categories = [
  {
    id: 1,
    title: "Job Responsibilities",
    description: "Detailed breakdown of your daily tasks, project ownership, and accountability areas.",
    icon: "Briefcase",
    itemCount: 12,
    locked: true
  },
  {
    id: 2,
    title: "Skill Development Paths",
    description: "Structured learning roadmaps for technical and soft skills relevant to your career growth.",
    icon: "GraduationCap",
    itemCount: 8,
    locked: true
  },
  {
    id: 3,
    title: "Career Progression",
    description: "Clear pathways from junior to senior roles with required competencies and timelines.",
    icon: "TrendingUp",
    itemCount: 6,
    locked: true
  },
  {
    id: 4,
    title: "Team Workflows",
    description: "Standard operating procedures for sprint planning, deployments, and incident management.",
    icon: "GitBranch",
    itemCount: 15,
    locked: true
  }];


  const videoTutorials = [
  {
    id: 1,
    title: "Introduction to Our Tech Stack",
    duration: "12:34",
    thumbnail: "https://images.unsplash.com/photo-1516116216624-53e697fedbea",
    thumbnailAlt: "Modern computer screen displaying colorful code editor with multiple programming languages in dark theme workspace",
    instructor: "Sarah Chen, Senior Developer",
    locked: true
  },
  {
    id: 2,
    title: "Git Workflow & Branching Strategy",
    duration: "18:45",
    thumbnail: "https://images.unsplash.com/photo-1618335829571-29364e748b9a",
    thumbnailAlt: "Developer hands typing on laptop keyboard with Git version control interface visible on screen showing branch diagrams",
    instructor: "Marcus Rodriguez, Tech Lead",
    locked: true
  },
  {
    id: 3,
    title: "Debugging Techniques & Tools",
    duration: "25:12",
    thumbnail: "https://images.unsplash.com/photo-1551419762-4a3d998f6292",
    thumbnailAlt: "Close-up of debugging console with error messages and stack traces on dark background with code snippets",
    instructor: "Emily Watson, Staff Engineer",
    locked: true
  }];


  const downloadableResources = [
  {
    id: 1,
    title: "Developer Handbook 2025",
    fileType: "PDF",
    fileSize: "2.4 MB",
    description: "Comprehensive guide covering coding standards, architecture patterns, and team conventions.",
    locked: true
  },
  {
    id: 2,
    title: "API Documentation Template",
    fileType: "DOCX",
    fileSize: "156 KB",
    description: "Standardized template for documenting REST APIs with examples and best practices.",
    locked: true
  },
  {
    id: 3,
    title: "Code Review Checklist",
    fileType: "PDF",
    fileSize: "324 KB",
    description: "Essential checklist for conducting thorough code reviews covering security, performance, and maintainability.",
    locked: true
  }];


  const upcomingFeatures = [
  "Searchable knowledge base with instant answers to common questions",
  "Interactive learning modules with quizzes and hands-on exercises",
  "Video tutorials from experienced team members and industry experts",
  "Downloadable resources including templates, checklists, and guides",
  "Role-specific workflows with step-by-step process documentation",
  "Performance tracking with skill assessments and progress reports",
  "Personalized learning paths based on your career goals and interests"];


  return (
    <div className="min-h-screen bg-background">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <XPProgressHeader
        currentXP={0}
        level="Newbie"
        totalXP={1000}
        recentGain={0} />

      <main className={`pt-20 transition-all duration-300 ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="mb-8">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <button
                onClick={() => navigate('/main-dashboard')}
                className="hover:text-foreground transition-colors duration-150">

                Dashboard
              </button>
              <Icon name="ChevronRight" size={16} color="var(--color-muted-foreground)" />
              <span className="text-foreground font-medium">Role Playbook</span>
            </div>
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">Role Playbook Hub</h1>
                <p className="text-muted-foreground">Your comprehensive guide to role-specific knowledge and best practices</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 bg-warning/10 text-warning px-4 py-2 rounded-lg border border-warning/20">
                  <Icon name="Lock" size={18} color="var(--color-warning)" />
                  <span className="text-sm font-medium">Coming Soon</span>
                </div>
              </div>
            </div>

            <RoleSelector currentRole="Junior Developer" locked={true} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Search role-specific documentation, guides, and resources..." />

            </div>
            <ProgressTracker completed={0} total={24} locked={true} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 space-y-6">
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-foreground">Featured Content</h2>
                  <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
                    View All
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contentPreviews?.map((content) =>
                  <ContentPreviewCard key={content?.id} {...content} />
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground mb-4">Knowledge Categories</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {categories?.map((category) =>
                  <CategorySection key={category?.id} {...category} />
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground mb-4">Video Tutorials</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {videoTutorials?.map((video) =>
                  <VideoTutorialCard key={video?.id} {...video} />
                  )}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-bold text-foreground mb-4">Downloadable Resources</h2>
                <div className="space-y-3">
                  {downloadableResources?.map((resource) =>
                  <DownloadableResource key={resource?.id} {...resource} />
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <FeatureHighlight features={upcomingFeatures} />
              
              <div className="bg-card border border-border rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="Lightbulb" size={20} color="var(--color-accent)" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">Quick Tips</h3>
                </div>
                <ul className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="shrink-0 mt-0.5" />
                    <span>Bookmark important resources for quick access</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="shrink-0 mt-0.5" />
                    <span>Complete learning modules to earn XP and level up</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-success)" className="shrink-0 mt-0.5" />
                    <span>Ask your AI assistant for personalized guidance</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-primary/10 to-accent/10 border border-primary/20 rounded-lg p-6 text-center">
                <Icon name="Rocket" size={32} color="var(--color-primary)" className="mx-auto mb-3" />
                <h3 className="text-base font-semibold text-foreground mb-2">Full Release Coming</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  This advanced knowledge management system will be available in the next release with full functionality.
                </p>
                <Button variant="outline" size="sm" fullWidth iconName="Bell" iconPosition="left">
                  Notify Me
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <AIChatbot />
    </div>);

};

export default RolePlaybookHub;